import { Subscription } from '@libs/subscription';

import { VideoLog } from '@features/video/lib/log';
import { MicWatchdog } from '@features/video/lib/micWatchdog';
import { CleanAudio, NoiseSuppressor, RNNOISE_SUPPORTED } from '@features/video/lib/rnnoise';
import { MicIssue } from '@features/video/types';

export type MicSourceEvent =
  // новый чистый трек: клиентам комнат раздать клоны
  | { type: 'track'; track: MediaStreamTrack }
  | { type: 'issue'; issue: MicIssue }
  // трек для индикатора уровня: отвод цепочки, мьют его не касается; null — цепочки нет
  | { type: 'meter'; track: MediaStreamTrack | null };

// Один микрофон судьи на все комнаты: сырой трек → шумодав → чистый трек. Каждый клиент
// комнаты публикует клон чистого трека: мьют и остановка клона остальных не касаются.
// Сторож чинит цепочку после прерывания и раздает новые клоны событием
export class MicSource {
  readonly events = new Subscription<MicSourceEvent>();

  private readonly rnnoise = RNNOISE_SUPPORTED ? new NoiseSuppressor() : null;
  private readonly watchdog: MicWatchdog;
  private raw: MediaStreamTrack | null = null;
  private clean: CleanAudio | null = null;
  private micOn = true;

  constructor(private readonly log: VideoLog) {
    this.watchdog = new MicWatchdog({
      rnnoise: this.rnnoise,
      raw: () => this.raw,
      active: () => this.raw !== null,
      micOn: () => this.micOn,
      onIssue: (issue) => this.emit({ type: 'issue', issue }),
      onRebuilt: async (clean) => this.adopt(clean),
      log: () => this.log,
    });
    if (this.rnnoise) this.rnnoise.onState = () => this.watchdog.check();
  }

  // Разбудить аудиоконтекст: синхронно из клика, до первых await
  wake(): void {
    this.rnnoise?.wake();
  }

  // Тяжелая загрузка воркета заранее, пока идет вход
  load(): void {
    this.rnnoise?.load().catch((e: unknown) => this.log.error('rnnoise_load_failed', e));
  }

  // Контекст шумодава ждет клика: клиентам держать трек в эфире замьюченным
  get gestureNeeded(): boolean {
    return this.clean !== null && this.rnnoise?.isRunning === false;
  }

  get meterTrack(): MediaStreamTrack | null {
    return this.clean?.meter ?? null;
  }

  // Сырой трек от хука устройств (первый вход, новое устройство): цепочка заново, чистый
  // трек — событием. Без трека источник пуст
  async attach(raw: MediaStreamTrack | null): Promise<void> {
    this.watchdog.stop();
    this.raw = raw;
    this.clean = null;
    if (!raw) {
      this.emit({ type: 'meter', track: null });
      return;
    }
    if (this.rnnoise) {
      try {
        this.clean = await this.rnnoise.attach(raw);
      } catch {
        // шумодав не собрался: в эфир идет сырой звук
      }
    }
    this.emit({ type: 'meter', track: this.clean?.meter ?? null });
    this.emit({ type: 'track', track: this.clean?.track ?? raw });
    this.watchdog.start();
  }

  // Трек для публикации в одну комнату
  clone(): MediaStreamTrack | null {
    const track = this.clean?.track ?? this.raw;
    return track ? track.clone() : null;
  }

  // Намерение судьи: включение — жест, им будим контекст и перепроверяем
  setMicOn(on: boolean): void {
    this.micOn = on;
    if (on) this.watchdog.wake();
  }

  // Клик по плашке «Включить микрофон»
  wakeMic(): void {
    this.watchdog.wake();
  }

  destroy(): void {
    this.watchdog.stop();
    this.rnnoise?.destroy();
    this.raw = null;
    this.clean = null;
  }

  /*
      ____,-------------------------------,____
      \   |            Private            |   /
      /___|-------------------------------|___\
  */

  private adopt(clean: CleanAudio): void {
    this.clean = clean;
    this.emit({ type: 'meter', track: clean.meter });
    this.emit({ type: 'track', track: clean.track });
  }

  private emit(event: MicSourceEvent): void {
    this.events.publish(event);
  }
}
